import { useTranslation } from 'next-i18next';

import { type PlanType } from '@/types';

interface FeatureItem {
  text: string;
  hidden?: boolean;
}

const filterVisibleFeatures = (features: FeatureItem[]) =>
  features.filter(feature => !feature.hidden).map(feature => feature.text);

export const useFeatureStrings = (
  countryCode: string,
): Record<PlanType, { normalFeatures: string[]; aiFeatures: string[] }> => {
  const { t } = useTranslation(['common']);

  const KR_FREE_FEATURES: FeatureItem[] = [
    { text: t('common:planFeatures.free.useFreeMap') },
    { text: t('common:planFeatures.free.createPublicQuiz') },
    { text: t('common:planFeatures.free.quizLibrary') },
    { text: t('common:planFeatures.free.concurrentUsers') },
  ];

  const KR_FREE_AI_FEATURES: FeatureItem[] = [];

  const KR_BASIC_FEATURES: FeatureItem[] = [
    { text: t('common:planFeatures.basic.useBasicMap') },
    { text: t('common:planFeatures.basic.allowPrivateQuiz') },
  ];

  const KR_BASIC_AI_FEATURES: FeatureItem[] = [
    { text: t('common:planFeatures.basic.allowBuildAiQuiz') },
  ];

  const KR_PRO_FEATURES: FeatureItem[] = [
    { text: t('common:planFeatures.unlimitedMapUsage') },
    { text: t('common:planFeatures.allLearningReport') },
    { text: t('common:planFeatures.continueNextQuiz') },
    { text: t('common:planFeatures.saveWorksheet') },
  ];

  const KR_PRO_AI_FEATURES: FeatureItem[] = [
    { text: t('common:planFeatures.aiTutor') },
    { text: t('common:planFeatures.multilingualTranslation') },
    { text: t('common:planFeatures.createSimilarQuestions') },
    { text: t('common:planFeatures.createChoicesAndAnswers') },
  ];

  const GLOBAL_FREE_FEATURES: FeatureItem[] = [
    { text: t('common:planFeatures.free.useFreeMap') },
    { text: t('common:planFeatures.free.createPublicQuiz') },
    { text: t('common:planFeatures.free.quizLibrary') },
    { text: t('common:planFeatures.free.concurrentUsers') },
  ];

  const GLOBAL_FREE_AI_FEATURES: FeatureItem[] = [];

  const GLOBAL_BASIC_FEATURES: FeatureItem[] = [
    { text: t('common:planFeatures.basic.useBasicMap') },
    { text: t('common:planFeatures.basic.allowPrivateQuiz') },
    {
      text: t('common:planFeatures.basic.removeAd'),
      hidden: countryCode !== 'ID',
    },
  ];

  const GLOBAL_BASIC_AI_FEATURES: FeatureItem[] = [
    { text: t('common:planFeatures.basic.allowBuildAiQuiz') },
  ];

  const GLOBAL_PRO_FEATURES: FeatureItem[] = [
    { text: t('common:planFeatures.unlimitedMapUsage') },
    { text: t('common:planFeatures.allLearningReport') },
    { text: t('common:planFeatures.continueNextQuiz') },
    { text: t('common:planFeatures.saveWorksheet') },
  ];

  const GLOBAL_PRO_AI_FEATURES: FeatureItem[] = [
    { text: t('common:planFeatures.multilingualTranslation') },
    { text: t('common:planFeatures.createSimilarQuestions') },
    { text: t('common:planFeatures.createChoicesAndAnswers') },
  ];

  const isKorea = countryCode === 'KR';

  const freeFeatures = isKorea ? KR_FREE_FEATURES : GLOBAL_FREE_FEATURES;
  const freeAiFeatures = isKorea
    ? KR_FREE_AI_FEATURES
    : GLOBAL_FREE_AI_FEATURES;
  const basicFeatures = isKorea ? KR_BASIC_FEATURES : GLOBAL_BASIC_FEATURES;
  const basicAiFeatures = isKorea
    ? KR_BASIC_AI_FEATURES
    : GLOBAL_BASIC_AI_FEATURES;
  const proFeatures = isKorea ? KR_PRO_FEATURES : GLOBAL_PRO_FEATURES;
  const proAiFeatures = isKorea ? KR_PRO_AI_FEATURES : GLOBAL_PRO_AI_FEATURES;

  return {
    FREE: {
      normalFeatures: filterVisibleFeatures(freeFeatures),
      aiFeatures: filterVisibleFeatures(freeAiFeatures),
    },
    BASIC: {
      normalFeatures: filterVisibleFeatures(basicFeatures),
      aiFeatures: filterVisibleFeatures(basicAiFeatures),
    },
    PRO: {
      normalFeatures: filterVisibleFeatures(proFeatures),
      aiFeatures: filterVisibleFeatures(proAiFeatures),
    },
  };
};
